/**
 * Roles (Configuration) — the typing centre's custom roles; moved here from the Team page.
 *
 * Route: /settings/roles
 * "Seed default roles" creates the missing defaults by name (Commission Agent, Accountant, Manager, Viewer); they
 * can be edited afterwards like any custom role.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, AutoAwesome as SeedIcon } from '@mui/icons-material';
import { fontSize as fs } from '../../utils/accounting/styles';
import ApiService from '../../services/api.service';
import RolesTab from '../../components/team/RolesTab';
import RoleDialog from '../../components/team/RoleDialog';
import { serverMessage } from '../../utils/apiConvert';

const Roles = ({ currentUser, embedded = false, onRolesChanged }) => {
  const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesResp, usersResp] = await Promise.all([
        ApiService.getRoles({ includeArchived: true }),
        ApiService.get(`/api/users/typing-center/${typingCenterId}`),
      ]);
      setRoles(rolesResp?.data?.data || []);
      setUsers(usersResp?.data?.data || []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load roles'));
    } finally {
      setLoading(false);
    }
  }, [typingCenterId]);

  useEffect(() => { if (typingCenterId) load(); }, [typingCenterId, load]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) await ApiService.updateRole(editing.id, payload);
      else await ApiService.createRole(payload);
      setSuccess(editing ? 'Role updated' : 'Role created');
      setDialogOpen(false);
      setEditing(null);
      load();
      onRolesChanged?.();
    } finally { setSaving(false); }
  };

  const handleArchive = async (role) => {
    if (!window.confirm(`Archive the role "${role.name}"? It will no longer be assignable.`)) return;
    try {
      await ApiService.archiveRole(role.id);
      setSuccess('Role archived');
      load();
      onRolesChanged?.();
    } catch (err) {
      setError(serverMessage(err, 'Could not archive the role'));
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const res = await ApiService.post('/api/roles/seed-defaults');
      const { created = 0, skipped = 0 } = res?.data?.data || {};
      setSuccess(created > 0 ? `Created ${created} default role${created === 1 ? '' : 's'}${skipped ? ` (${skipped} already existed)` : ''}` : 'All default roles already exist');
      load();
      onRolesChanged?.();
    } catch (err) {
      setError(serverMessage(err, 'Could not seed the default roles'));
    } finally {
      setSeeding(false);
    }
  };

  const activeCount = roles.filter((r) => r.status !== 'ARCHIVED').length;

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          {!embedded && <Typography variant="h5" fontWeight={600} color="secondary.main">Roles</Typography>}
          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
            {activeCount} active role{activeCount === 1 ? '' : 's'} · Default roles: Commission Agent, Accountant, Manager, Viewer
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Refresh"><IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          <Button size="small" startIcon={seeding ? <CircularProgress size={16} /> : <SeedIcon />} onClick={seed}
            disabled={seeding} sx={{ textTransform: 'none', fontSize: fs.sm }}>
            Seed default roles
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setEditing(null); setDialogOpen(true); }}
            sx={{ textTransform: 'none', fontSize: fs.sm }}>
            Create Role
          </Button>
        </Box>
      </Box>


      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <RolesTab
          roles={roles}
          users={users}
          showArchived={showArchived}
          onToggleArchived={setShowArchived}
          onCreate={() => { setEditing(null); setDialogOpen(true); }}
          onEdit={(r) => { setEditing(r); setDialogOpen(true); }}
          onArchive={handleArchive}
        />
      )}

      <RoleDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={handleSave} role={editing} currentUser={currentUser} saving={saving} />
    </Box>
  );
};

export default Roles;
